import React, { useEffect, useRef } from "react";
import L from "leaflet";

const GRADE = { A: "#00FF7F", B: "#84cc16", C: "#F59E0B", D: "#F97316", E: "#EF4444", "—": "#475569" };
export const STAGE = { appraisal_review: "#8A2BE2", appraised: "#00FFFF", funding: "#F59E0B", construction: "#F97316", completed: "#00FF7F", declined: "#EF4444" };
const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const f0 = (n) => (typeof n === "number" ? Math.round(n).toLocaleString() : "—");

export const JusticeMap = ({ cities = [], selected, onSelect, renewals = [], onSelectRenewal, height = 340, center = [39.0, 35.3], zoom = 6, testid = "justice-map" }) => {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const renewalRef = useRef(null);

  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const map = L.map(ref.current, { zoomControl: false, attributionControl: false, scrollWheelZoom: false }).setView(center, zoom);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 12, attribution: "© OpenStreetMap" }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    renewalRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.clearLayers();
    cities.forEach((c) => {
      const color = GRADE[c.grade] || GRADE["—"];
      const r = c.index == null ? 7 : 8 + c.index / 8;
      const m = L.circleMarker([c.lat, c.lng], { radius: r, color, weight: selected?.city === c.city ? 3 : 1.5, fillColor: color, fillOpacity: c.index == null ? 0.15 : 0.55 })
        .bindTooltip(`<div style="font-family:monospace;font-size:11px"><b>${esc(c.city)}</b> · ${c.index ?? "no data"} ${esc(c.grade)}<br/>${Object.entries(c.pillars || {}).map(([k, v]) => `${esc(k)} ${v}`).join(" · ") || "unranked"}</div>`, { direction: "top", opacity: 0.95 })
        .on("click", () => onSelect?.(c));
      layer.addLayer(m);
    });
  }, [cities, selected, onSelect]);

  useEffect(() => {
    const layer = renewalRef.current;
    if (!layer) return;
    layer.clearLayers();
    renewals.forEach((r) => {
      const color = STAGE[r.status] || "#94a3b8";
      const m = L.marker([r.lat, r.lng], { icon: L.divIcon({ className: "", iconSize: [14, 14], iconAnchor: [7, 7], html: `<div style="width:14px;height:14px;transform:rotate(45deg);background:${color};border:1.5px solid #050A15;box-shadow:0 0 8px ${color}"></div>` }) })
        .bindTooltip(`<div style="font-family:monospace;font-size:11px"><b>${esc(r.title)}</b><br/>${esc(r.district)}, ${esc(r.city)} · ${esc(r.track)} · <span style="color:${color}">${esc(r.status.replace("_", " "))}</span><br/>UAD ${esc(r.uad)} · risk ${r.risk_score} · ${r.units} units<br/>${f0(r.as_is_value_try)} → ${f0(r.as_renewed_value_try)} TRY${r.bond_pct != null ? ` · bond ${r.bond_pct}%` : ""}</div>`, { direction: "top", opacity: 0.95 })
        .on("click", () => onSelectRenewal?.(r));
      layer.addLayer(m);
    });
  }, [renewals, onSelectRenewal]);

  return (
    <div className="relative qs-panel overflow-hidden" data-testid={testid}>
      <div ref={ref} style={{ height }} />
      <div className="absolute bottom-2 left-3 flex flex-wrap gap-2 text-[10px] font-mono z-[400]">
        {cities.length > 0 && Object.entries(GRADE).map(([g, col]) => <span key={g} className="flex items-center gap-1 text-slate-400"><span className="h-2.5 w-2.5 rounded-full" style={{ background: col }} /> {g === "—" ? "no data" : g}</span>)}
        {renewals.length > 0 && Object.entries(STAGE).map(([s, col]) => <span key={s} className="flex items-center gap-1 text-slate-400" data-testid={`legend-${s}`}><span className="h-2 w-2 rotate-45" style={{ background: col }} /> {s.replace("_", " ")}</span>)}
      </div>
    </div>
  );
};
